import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router'

import { Button, IconRefreshCw01, Tooltip } from '@/components'
import { useToastNotifications, useAnalytics, useAuth } from '@/hooks'
import { ToastNotification, Warranty } from '@/models'

type RefreshMonitoringButtonProps = {
    warranty: Warranty
    onChange: () => void
}

const RefreshMonitoringButton = ({
    warranty,
    ...props
}: RefreshMonitoringButtonProps) => {
    const auth = useAuth()
    const { trackEvent } = useAnalytics()
    const location = useLocation()
    const { success } = useToastNotifications()
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const [notification, setNotification] = useState<ToastNotification | null>(null)
    const [processing, setProcessing] = useState(false)
    const [isDisabled, setIsDisabled] = useState(false)

    const handleRefresh = async () => {
        if (auth.user?.role.isAdminOrStaff && warranty.homeowner.system.manufacturer.isTesla) {
            setIsDisabled(true)
        }
        await warranty.monitoringUpdate()
        await props.onChange()
        intervalRef.current = setInterval(props.onChange, 5000)
    }

    const handleManualRefresh = async () => {
        trackEvent('button_click', 'monitoring', 'Refresh Monitoring Button')
        setProcessing(true)
        try {
            await handleRefresh()
            const notification = success(
                'Update Queued: Your request for fresh data is being processed. The page will automatically refresh with the latest information shortly.',
                undefined, 'permanent'
            )
            setNotification(notification)
        } finally {
            setProcessing(false)
        }
    }

    const handleAutoRefresh = async () => {
        if (warranty.refresh_allowed && location.pathname.includes('monitoring/energy-graph')) {
            await handleRefresh()
            success('Monitoring data is being refreshed and will update automatically.')
        }
    }

    useEffect(() => {
        handleAutoRefresh()
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [])

    useEffect(() => () => {
        notification?.close()
    }, [notification])

    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
            notification?.close()
            if (auth.user?.role.isAdminOrStaff && warranty.homeowner.system.manufacturer.isTesla) {
                setIsDisabled(false)
            }
            success('System successfully refreshed.')
        }
    }, [warranty.homeowner.monitoring_data?.raw.updated_at])

    const tooltip = useMemo(() => {
        if (warranty.homeowner.system.manufacturer.isTesla) {
            if (warranty.refresh_allowed && !isDisabled) {
                return <>
                    Clicking this button will check Tesla Powerhub for new system data. It will either help connect data
                    for a system currently not connected on Daybreak, or pull fresh data for a connected warranty. You
                    can only use it once per hour, so please make sure the site is shared on Powerhub and the DIN is
                    accurate on the warranty details tab before clicking.
                </>
            }
            if (auth.user?.role.isAdminOrStaff) {
                return <>
                    We’re checking for new monitoring data now. Before retrying, make sure the site is shared on Tesla
                    Powerhub and the DIN is correct on the warranty. You can run it again once this completes.
                </>
            }
            return <>
                You can use this button once per hour for Tesla systems. We’re checking for new monitoring data now.
                Before retrying, make sure the site is shared on Tesla Powerhub and the DIN is correct on the warranty.
            </>
        }

        return <>
            Refresh Limit Reached: Users are allowed a single daily refresh of system data to optimize system
            performance and manage monitoring expenses. This daily quota resets every 24 hours. Should you require
            additional data updates within the same day, please view the most recent data in the system history or log
            into the manufacturer's portal for real-time information. For further assistance, contact your account
            manager or <a href="mailto:monitoring@solarinsure.com">monitoring@solarinsure.com.</a>
        </>
    }, [warranty.refresh_allowed, isDisabled])

    return <Tooltip
        interactive
        disabled={warranty.refresh_allowed && !warranty.homeowner.system.manufacturer.isTesla}
        content={tooltip}
    >
        <Button
            design="btn-secondary-gray"
            onClick={handleManualRefresh}
            disabled={!warranty.refresh_allowed || isDisabled}
            processing={processing}
            className="gap-2"
        >
            <IconRefreshCw01/> Refresh Monitoring
        </Button>
    </Tooltip>
}

export default RefreshMonitoringButton
